import { watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRafFn } from '@vueuse/core';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { useMapStyleStore } from 'shared/mapStyles/stores/mapStyleStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useShapeStore } from 'shared/shapes/stores/shapeStore';
import { usePackageStore } from 'shared/packages/stores/packageStore';
import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import type { MapStyle } from 'shared/mapStyles/domain/mapStyle';
import type { PackageContext } from 'shared/mapStyles/domain/packageContext';
import type { Package } from 'shared/packages/domain/package';
import type { StorageArea } from 'shared/storageAreas/domain/storageArea';
import { getShapeBasis, computePlaceGeometry } from '../domain/placeGeometry';
import { centeredFootprintRing } from '../domain/footprint';
import { padExtent, pointInExtent, type Extent } from '../domain/viewportCulling';
import { placeStyle } from '../styles/placeStyle';

const DEFAULT_DIMENSION_MM = 1000;

// Extra viewport margin (fraction of view size) kept rendered on each side.
const VIEWPORT_PADDING = 0.25;

export interface PackagesLayerOptions {
  mapStyle?: Ref<MapStyle>;
  /** When it returns false, map clicks are ignored by the package click listeners. */
  clickEnabled?: () => boolean;
}

/** Receives the clicked package id, or null when a click hit no package. */
export type PackagesLayerClickListener = (packageId: string | null) => void;

/**
 * Renders placed packages at their independent geographic position as a single
 * rotated footprint (width x length), styled by the map style's package style.
 * Legacy packages without a stored position fall back to the centroid of their
 * occupied storage places so they stay visible.
 */
export function usePackagesLayer(options?: PackagesLayerOptions) {
  const mapStyleStore = useMapStyleStore();
  const storageAreaStore = useStorageAreaStore();
  const shapeStore = useShapeStore();
  const packageStore = usePackageStore();

  const source = new VectorSource();
  const selectedStyle = getMapStyle();

  let olMap: OLMap | null = null;
  let renderScheduled = true;
  const clickListeners: PackagesLayerClickListener[] = [];

  shapeStore.changed.on(() => scheduleRender());
  packageStore.changed.on(() => scheduleRender());
  watch(storageAreaStore, () => scheduleRender());
  watch(selectedStyle, () => scheduleRender());

  useRafFn(() => {
    if (!renderScheduled) return;
    renderScheduled = false;
    rebuild();
  });

  return {
    attach,
    addClickListener,
  };

  function addClickListener(listener: PackagesLayerClickListener) {
    clickListeners.push(listener);
  }

  function getMapStyle() {
    if (options?.mapStyle) return options.mapStyle;
    const { selectedStyle } = storeToRefs(mapStyleStore);
    return selectedStyle;
  }

  function scheduleRender() {
    renderScheduled = true;
  }

  function attach(map: OLMap) {
    olMap = map;
    const layer = new VectorLayer({
      source,
      style: placeStyle,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    });
    layer.setZIndex(11); // above the places layer (10)
    map.addLayer(layer);

    // Re-cull to the new viewport after every pan/zoom.
    map.on('moveend', () => scheduleRender());

    // Must be registered before the shape layer's click listener so preventDefault() stops shape selection.
    map.on('click', (ev) => {
      if (clickListeners.length === 0) return;
      if (ev.defaultPrevented) return;
      const original = ev.originalEvent as MouseEvent;
      if (original.altKey || original.ctrlKey || original.metaKey) return;
      if (options?.clickEnabled && !options.clickEnabled()) return;

      const hit = source
        .getFeaturesAtCoordinate(ev.coordinate)
        .sort((a, b) => (b.get('stackLevel') ?? 0) - (a.get('stackLevel') ?? 0))[0];
      const packageId: string | null = hit?.get('packageId') ?? null;

      if (packageId) ev.preventDefault();
      for (const listener of clickListeners) listener(packageId);
    });

    rebuild();
    return source;
  }

  function getPaddedExtent(): Extent | null {
    if (!olMap) return null;
    const size = olMap.getSize();
    if (!size) return null;
    const extent = olMap.getView().calculateExtent(size) as Extent;
    return padExtent(extent, VIEWPORT_PADDING);
  }

  function rebuild() {
    source.clear();

    const packageStyleFns = selectedStyle.value?.package;
    if (!packageStyleFns) return;

    const paddedExtent = getPaddedExtent();

    for (const pkg of packageStore.packages) {
      if (!pkg.packageStorageAreaId) continue; // carried / not placed

      // Cull packages with a known position before computing their footprint.
      if (paddedExtent && pkg.longitude != null && pkg.latitude != null) {
        if (!pointInExtent([pkg.longitude, pkg.latitude], paddedExtent)) continue;
      }

      const footprint = computeFootprint(pkg);
      if (!footprint) continue;

      const area = storageAreaStore.getById(pkg.packageStorageAreaId) ?? null;
      const context = buildContext(pkg, area);

      if (!packageStyleFns.visible(context)) continue;

      const feature = new Feature({
        geometry: new Polygon([footprint]),
      });

      feature.setId(`package-${pkg.id}`);
      feature.setProperties({
        packageId: pkg.id,
        storageAreaId: pkg.packageStorageAreaId,
        stackLevel: pkg.stackLevel ?? 0,
        label: packageStyleFns.label(context),
        labelAlongLength: true,
        outlineColor: packageStyleFns.outlineColor(context),
        outlineSize: packageStyleFns.outlineSize(context),
        textAlignment: packageStyleFns.textAlignment(context),
        textPosition: packageStyleFns.textPosition(context),
        textOpacity: packageStyleFns.textOpacity(context),
        textColor: packageStyleFns.textColor(context),
        textSize: packageStyleFns.textSize(context),
        backgroundColor: packageStyleFns.backgroundColor(context),
        backgroundPattern: packageStyleFns.backgroundPattern(context),
        backgroundOpacity: packageStyleFns.backgroundOpacity(context),
      });

      source.addFeature(feature);
    }
  }

  function computeFootprint(pkg: Package): number[][] | null {
    const widthMeters = (pkg.width ?? DEFAULT_DIMENSION_MM) / 1000;
    const lengthMeters = (pkg.length ?? pkg.width ?? DEFAULT_DIMENSION_MM) / 1000;

    if (pkg.longitude != null && pkg.latitude != null) {
      return centeredFootprintRing([pkg.longitude, pkg.latitude], widthMeters, lengthMeters, pkg.rotation ?? 0);
    }

    const legacy = computeLegacyPlacement(pkg);
    if (!legacy) return null;
    return centeredFootprintRing(legacy.center, widthMeters, lengthMeters, legacy.rotation);
  }

  function computeLegacyPlacement(pkg: Package): { center: [number, number]; rotation: number } | null {
    const area = storageAreaStore.getById(pkg.packageStorageAreaId!);
    if (!area) return null;

    const places = area.packageStorageData?.places ?? area.packagePlaceData?.places ?? [];
    const occupied = places.filter((place) => pkg.occupiedStoragePlaceIds.includes(place.id));
    if (occupied.length === 0) return null;

    const shapeFeature = shapeStore.getByStorageAreaId(area.id)[0]?.shapeGeoJson;
    if (!shapeFeature) return null;

    const basis = getShapeBasis(shapeFeature);
    if (!basis) return null;

    const geometries = computePlaceGeometry(shapeFeature, occupied);
    let sumLon = 0;
    let sumLat = 0;
    let count = 0;
    for (const geometry of geometries) {
      for (const coordinate of geometry.coordinates) {
        sumLon += coordinate[0]!;
        sumLat += coordinate[1]!;
        count += 1;
      }
    }
    if (count === 0) return null;

    const center: [number, number] = [sumLon / count, sumLat / count];
    const cosLat = Math.cos((center[1] * Math.PI) / 180);
    const rotation = (((Math.atan2(basis.widthVector[0]! * cosLat, basis.widthVector[1]!) * 180) / Math.PI) % 360 + 360) % 360;

    return { center, rotation };
  }

  function buildContext(pkg: Package, area: StorageArea | null): PackageContext {
    return {
      index: 0,
      disabled: false,
      occupied: true,
      packageCount: 1,
      maxStackCount: null,
      maxWeight: null,
      maxHeight: null,
      x: 0,
      y: 0,
      length: pkg.length ?? 0,
      width: pkg.width ?? 0,
      zIndex: pkg.stackLevel ?? 0,
      accessDirections: 0,
      accessFront: false,
      accessRight: false,
      accessBack: false,
      accessLeft: false,
      storageArea: area
        ? {
            id: area.id,
            name: area.name,
            empty: area.status === StorageAreaStatus.Empty,
          }
        : null,
      packageId: pkg.id,
      packageNumber: pkg.number,
      packageTypeId: pkg.packageTypeId,
      packagingId: null,
      productId: null,
      weight: pkg.weight,
      height: pkg.height,
      stackLevel: pkg.stackLevel,
    };
  }
}
